import {useMemo, useState, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {getAuthorizationsOdonto} from '../services/getAuthorizationsOdonto';
import {AuthorizationsOdontoItem} from '../types';

const ITEMS_PER_PAGE = 12;

export const useAuthorizationsOdonto = () => {
	const {SEARCH_PARAMS} = useQueryParams();
	const {authorizationStatus} = SEARCH_PARAMS;
	const [displayedItemsCount, setDisplayedItemsCount] = useState(ITEMS_PER_PAGE);

	const {
		data: queryData,
		isLoading,
		isError,
		refetch,
	} = useQuery<AuthorizationsOdontoItem[]>({
		queryKey: [QUERY_KEYS.AUTHORIZATIONS_ODONTO],
		queryFn: getAuthorizationsOdonto,
	});

	const allItems = queryData || [];
	const hasActiveFilter = authorizationStatus && authorizationStatus !== 'todos';

	useEffect(() => {
		setDisplayedItemsCount(ITEMS_PER_PAGE);
	}, [authorizationStatus]);

	const {filteredItems, totalItems} = useMemo(() => {
		if (!hasActiveFilter) {
			return {
				filteredItems: allItems,
				totalItems: allItems.length,
			};
		}

		const filtered = allItems.filter(item => item.dsStatusItemAutor === authorizationStatus);
		return {
			filteredItems: filtered,
			totalItems: filtered.length,
		};
	}, [allItems, hasActiveFilter, authorizationStatus]);

	const displayedItems = useMemo(
		() => filteredItems.slice(0, displayedItemsCount),
		[filteredItems, displayedItemsCount],
	);

	const hasMoreItems = filteredItems.length > displayedItemsCount;

	const loadMore = () => {
		if (hasMoreItems) {
			setDisplayedItemsCount(prev => prev + ITEMS_PER_PAGE);
		}
	};

	const resetState = () => {
		setDisplayedItemsCount(ITEMS_PER_PAGE);
	};

	const refresh = () => {
		refetch();
	};

	const itemsListIsEmpty = allItems.length === 0 && !isLoading;
	const filteredItemsIsEmpty = filteredItems.length === 0;

	return {
		items: displayedItems,
		loadingItemsList: isLoading,
		isLoading,
		itemsListIsEmpty,
		itemsListHasError: isError,
		totalItems,
		loadMore,
		resetState,
		refresh,
		hasActiveFilter,
		filteredItemsIsEmpty,
		hasMoreItems,
	};
};
